/**
 * Daily group — a single date accordion in the daily feed. Renders a
 * heading with the date + count + caret, then a body of article cards.
 *
 * Expansion state is component-local (Round A does not need cross-group
 * persistence). The most recent day auto-expands on first render.
 */

import { el } from "../dom";
import { articleCard } from "./articleCard";
import type { DailyDayGroup } from "../types";

export interface DailyGroupOptions {
  group: DailyDayGroup;
  /** Whether the body is expanded at mount time. */
  initiallyExpanded?: boolean;
  /** Card filter callback — return false to hide the card. */
  filter?: (pmid: string) => boolean;
}

export function dailyGroup({ group, initiallyExpanded = false, filter }: DailyGroupOptions): HTMLElement {
  const visibleItems = filter
    ? group.items.filter((it) => filter(it.pmid))
    : group.items;

  const totalCount = group.items.length;
  const visibleCount = visibleItems.length;

  const body = el("div", { class: "day-group-body" }, [
    visibleItems.length === 0
      ? el("p", { class: "muted" }, "（该日文献均被筛选过滤）")
      : null,
    ...visibleItems.map((item, idx) => articleCard({ item, ordinal: idx + 1 })),
  ]);

  const headerButton = el(
    "button",
    {
      class: "day-group-header",
      type: "button",
      "aria-expanded": initiallyExpanded ? "true" : "false",
    },
    [
      el("div", { class: "day-group-header-text" }, [
        el("span", { class: "day-group-date" }, group.epdat),
        el("span", { class: "day-group-weekday" }, weekdayFor(group.epdat)),
      ]),
      el("div", { class: "day-group-count" }, [
        el("span", null, `${visibleCount} / ${totalCount} 篇`),
        el("span", { class: "day-group-caret" }, "▶"),
      ]),
    ],
  );

  const root = el(
    "section",
    {
      class: `day-group ${initiallyExpanded ? "is-expanded" : "is-collapsed"}`,
      "data-epdat": group.epdat,
    },
    [headerButton, body],
  );

  headerButton.addEventListener("click", () => {
    const nowExpanded = root.classList.toggle("is-expanded");
    root.classList.toggle("is-collapsed", !nowExpanded);
    headerButton.setAttribute("aria-expanded", nowExpanded ? "true" : "false");
  });

  return root;
}

function weekdayFor(iso: string): string {
  // iso = YYYY-MM-DD; treat as UTC to avoid tz-driven off-by-one in
  // tests / static analysis.
  const parts = iso.split("-").map((s) => Number.parseInt(s, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) return "";
  const dt = new Date(Date.UTC(y, m - 1, d));
  const names = ["周日", "周一", "周二", "周三", "周四", "周五", "周六"];
  const idx = dt.getUTCDay();
  return names[idx] ?? "";
}
